import { useRef } from 'react';
import { arrivedFromInsideSection } from './previousLocation';

/**
 * Route prefixes that make up each staff table's "own section": the list route itself plus the
 * routes you reach by drilling into one of its rows (detail pages, edit wizards, sidebars).
 *
 * A table restores its persisted filters only when the previous route was inside its own section,
 * so list -> detail -> back keeps your filters while section -> other section -> back gives you a
 * clean table. Scoping to prefixes (rather than the exact detail route) means list -> detail ->
 * edit wizard -> back to the list still restores.
 *
 * Paths are taken from the router config in `Views/Frontend.tsx` and
 * `Views/Pages/FrontendAdmin/AdminPage.tsx`, not guessed.
 */
export const STAFF_TABLE_SECTIONS = {
	/**
	 * List: /staff-bookings. Rows open /bookings/:id, which edits via /booking-wizard/*, and the
	 * customer cell opens that customer's /users/:id/bookings tab.
	 */
	BOOKINGS: ['/staff-bookings', '/bookings', '/booking-wizard', '/event-booking-wizard', '/users'],
	/**
	 * List: /staff-bookings (parking tab). Rows open /parking-reservations/:id and the customer cell
	 * opens /users/:id/account.
	 */
	PARKING_RESERVATIONS: ['/staff-bookings', '/parking-reservations', '/users'],
	/**
	 * List: /staff-bookings (charters tab). Rows open the charter's sidebar on the staff schedule at
	 * /staff-schedule/charter/:id, and the customer cell opens /users/:id/bookings.
	 *
	 * Both of those open in a new tab, so returning to the list is a browser tab switch rather than a
	 * navigation — the prefixes matter for the case where the reader navigates away and comes back.
	 */
	CHARTERS: ['/staff-bookings', '/staff-schedule', '/users'],
	/**
	 * List: /transactions. No row-level detail page — cells link out to the paid-for record and to
	 * the customer's /users/:id/transactions tab.
	 */
	TRANSACTIONS: ['/transactions', '/bookings', '/parking-reservations', '/users'],
	/** List: /ferry-trips, with the edit drawer at /ferry-trips/edit/:id. */
	FERRY_TRIPS: ['/ferry-trips'],
	/** List: /vouchers. Rows open the purchaser's /users/:id/credits tab. */
	VOUCHERS: ['/vouchers', '/users'],
	/**
	 * List: /refunds. Rows open the refunded booking at /bookings/:id and the customer cell opens
	 * /users/:id/transactions.
	 */
	REFUNDS: ['/refunds', '/bookings', '/users'],
	/** List: /anytime-tickets. Rows open the holder's /users/:id/entitlements tab. */
	ANYTIME_TICKETS: ['/anytime-tickets', '/users'],
	/** List: /admin/booking-transfers. Cells link to the booking and to both owners. */
	BOOKING_TRANSFERS: ['/admin/booking-transfers', '/bookings', '/users'],
	/** List: /admin/anytime-ticket-transfers. Cells link to both owners. */
	ANYTIME_TICKET_TRANSFERS: ['/admin/anytime-ticket-transfers', '/users'],
	/** List: /users. Rows open the user detail at /users/:id/:tab?, so one prefix covers both. */
	USERS: ['/users'],
} as const satisfies Record<string, readonly string[]>;

/**
 * Resets a staff table's persisted filter store unless the user arrived from inside that table's
 * own section.
 *
 * IMPORTANT — call this BEFORE subscribing to the store (i.e. above the `useXStore()` call) in the
 * list component. The decision is made and applied during the FIRST RENDER, not in a mount effect,
 * for two reasons:
 *
 *  1. `getPreviousPathname()` is only meaningful during render (see `previousLocation.ts`).
 *  2. The reset has to land before the first data fetch. These lists build their react-query key
 *     from the store's filters; react-query starts fetching from its own mount effect. A reset
 *     performed in a competing `useEffect` would race that fetch and the table would flash
 *     filtered data before flipping to the defaults. Mutating the store during render, before this
 *     component reads it, means the very first render already computes the default query key.
 *
 * @param sectionPrefixes one of `STAFF_TABLE_SECTIONS`
 * @param resetFilters imperative reset, e.g. `() => useStaffXStore.getState().resetFilters()`
 * @param alwaysReset forces the reset regardless of the previous route (used by the embedded
 *   user-detail tables, which must always start from their own defaults)
 * @param onRestore optional counterpart to `resetFilters`, run instead of it when the persisted
 *   filters are being kept. Only needed by tables that share their store with an embedded copy and
 *   have to undo its interference (see the bookings table); must also run during render.
 */
export function useResetFiltersOnSectionEntry(
	sectionPrefixes: readonly string[],
	resetFilters: () => void,
	alwaysReset = false,
	onRestore?: () => void,
): void {
	// A ref latch rather than an effect: this must run while rendering. Resetting twice is
	// idempotent, so StrictMode's double render in development is harmless.
	const hasEvaluated = useRef(false);
	if (!hasEvaluated.current) {
		hasEvaluated.current = true;
		if (alwaysReset || !arrivedFromInsideSection(sectionPrefixes)) {
			resetFilters();
		} else {
			onRestore?.();
		}
	}
}
